import { IconButton } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';

import { IcDotSpread } from '@/assets/images';
import { FlagChips } from '@/components';
import { EthnicOriginList, RaceList } from '@/constants/form_inputs';
import { AddressStatus, IAddress, IMasterNamePerson } from '@/models';
import { maskSensitiveString } from '@/pages/MasterIndex/Persons/data';
import { getAddressLocation } from '@/services';
import { formatPersonName, formatPhoneNumber, generateUniqueId } from '@/utils';

type ColumnGenerator = () => Partial<GridColDef>;
type ColumnMap = Record<string, ColumnGenerator>;

export const InternalAffairsPeopleInvolvedCols = (
  openMenu?: (event: React.MouseEvent<HTMLButtonElement>, param: any) => void,
): ColumnMap => ({
  uniqueId: () => ({
    field: 'uniqueId',
    headerName: 'ID',
    minWidth: 120,
    renderCell: (params: GridRenderCellParams) => {
      const { uniqueId: uId } = params.row;
      return generateUniqueId('PER', uId);
    },
    valueGetter: (_, row) => generateUniqueId('PER', row.uniqueId),
  }),

  name: () => ({
    field: 'name',
    headerName: 'Name',
    minWidth: 200,
    renderCell: (params: GridRenderCellParams<IMasterNamePerson>) => {
      const { firstName, lastName } = params.row;
      return formatPersonName({ firstName, lastName }) || '/';
    },
  }),

  address: () => ({
    field: 'address',
    headerName: 'Address',
    minWidth: 200,
    renderCell: (params: GridRenderCellParams) => {
      let addressText = '/';

      // For records with an 'addresses' array
      if (params.row?.addresses?.length > 0) {
        const activeAddress = params.row.addresses.find(
          (item: IAddress) => item.status === AddressStatus.ACTIVE,
        );
        const addressObj = activeAddress || params.row.addresses[0];
        addressText = getAddressLocation(addressObj) || '/';
      }
      // For records with a single 'address' object
      else if (params.row?.address) {
        addressText = getAddressLocation(params.row.address) || '/';
      }

      return addressText;
    },
  }),

  flags: () => ({
    field: 'flags',
    headerName: 'Flags',
    minWidth: 150,
    sortable: false,
    filterable: false,
    renderCell: (params: GridRenderCellParams) => {
      const f = params.row?.flags || [];

      if (f.length === 0) {
        return '-';
      }

      const flagDetails = f.map((item: any) => ({
        _id: item._id,
        name: item.flag?.name,
        code: item.flag?.code,
        description: item.flag?.description || '',
        origin: item.origin,
        dateAssigned: item.dateAssigned,
        reason: item.reason,
      }));

      return (
        <FlagChips
          flags={flagDetails}
          size="small"
          maxDisplay={2}
          flexWrap="nowrap"
        />
      );
    },
  }),

  phone: () => ({
    field: 'phone',
    headerName: 'Phone number',
    minWidth: 200,
    renderCell: (params: GridRenderCellParams<IMasterNamePerson>) => {
      if (!params.row.phoneNumbers?.length) return '/';
      return params.row.phoneNumbers.map((item) => (
        <div key={item.phoneNumber}>
          {`${formatPhoneNumber(item.phoneNumber)} - ${item.type}`}
        </div>
      ));
    },
    valueGetter: (_, row) => row.phoneNumbers?.[0]?.phoneNumber,
  }),

  licenseId: () => ({
    field: 'licenseId',
    headerName: 'License ID',
    minWidth: 180,
    renderCell: (params: GridRenderCellParams) => {
      return (
        params.row.driverLicense?.number ||
        params.row.driverLicenseInfo?.number ||
        '/'
      );
    },
    valueGetter: (_, row) =>
      row.driverLicense?.number || row.driverLicenseInfo?.number || '',
  }),

  ethnicity: () => ({
    field: 'ethnicity',
    headerName: 'Ethnic Origin',
    minWidth: 180,
    renderCell: (params: GridRenderCellParams) =>
      EthnicOriginList[
        params.row.ethnicOrigin as keyof typeof EthnicOriginList
      ] || '/',
    valueGetter: (_, row) =>
      EthnicOriginList[row.ethnicOrigin as keyof typeof EthnicOriginList],
  }),

  race: () => ({
    field: 'race',
    headerName: 'Race',
    minWidth: 150,
    renderCell: (params: GridRenderCellParams) =>
      RaceList[params.row.race as keyof typeof RaceList] || '/',
  }),

  ssn: () => ({
    field: 'ssn',
    headerName: 'Social security number',
    minWidth: 200,
    renderCell: (params: GridRenderCellParams) => {
      return maskSensitiveString(params.row.ssn) || '/';
    },
    valueFormatter: (value: string | undefined | null) =>
      maskSensitiveString(value),
  }),

  actions: () => ({
    field: 'actions',
    headerName: 'Actions',
    sortable: false,
    filterable: false,
    minWidth: 80,
    renderCell: (params: GridRenderCellParams) => (
      <IconButton
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          if (openMenu) {
            openMenu(event, params.row);
          }
        }}
        aria-label="actions"
      >
        <IcDotSpread />
      </IconButton>
    ),
  }),
});
